import { useMemo, useState, useEffect } from "react"
import { useQuery } from "react-query"
import { useSearchParams } from "react-router-dom"
import { useAuthStore } from "../../stores/authStore"
import { ayudaApi } from "../../services/api"
import Table from "../../components/common/Table"
import Pagination from "../../components/common/Pagination"
import SearchBar from "../../components/common/SearchBar"
import StatusBadge from "../../components/common/StatusBadge"
import Button from "../../components/common/Button"
import Modal from "../../components/common/Modal"
import {
  ArrowDownTrayIcon,
  ClipboardDocumentListIcon,
  EyeIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline"

function ResidentAyudaPage() {
  const user = useAuthStore((state) => state.user)
  const [searchParams, setSearchParams] = useSearchParams()

  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [selectedAyuda, setSelectedAyuda] = useState(null)
  const [showFilters, setShowFilters] = useState(false)

  // Get values from URL search params
  const query = searchParams.get("search") || ""
  const currentPage = Number(searchParams.get("page")) || 1
  const perPage = Number(searchParams.get("per_page")) || 10
  const startDate = searchParams.get("start_date") || ""
  const endDate = searchParams.get("end_date") || ""
  const status = searchParams.get("status") || ""

  const residentId = user?.resident_id ?? user?.id ?? null

  // Update URL search params helper
  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams)

    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, String(value))
      } else {
        newParams.delete(key)
      }
    })

    setSearchParams(newParams)
  }

  const formatDateTime = (value) => {
    if (!value) return "-"
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return "-"

    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const formatDateOnly = (value) => {
    if (!value) return "-"
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return "-"

    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const requestParams = useMemo(
    () => ({
      page: currentPage,
      per_page: perPage,
      search: query || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      status: status || undefined,
    }),
    [currentPage, perPage, query, startDate, endDate, status]
  )

  const {
    data: ayudaPayload,
    error: ayudaError,
    isLoading,
    isFetching,
  } = useQuery(
    [
      "residentAyudaTransactions",
      residentId,
      requestParams.page,
      requestParams.per_page,
      requestParams.search || "",
      requestParams.start_date || "",
      requestParams.end_date || "",
      requestParams.status || "",
    ],
    async () => {
      if (!residentId) {
        throw new Error("Resident ID not found")
      }

      const res = await ayudaApi.getAyudaTransactionByResidentsId(residentId, requestParams)
      return res?.data?.data ?? res?.data ?? {}
    },
    {
      enabled: !!residentId,
      keepPreviousData: true,
    }
  )

  const { records, meta } = useMemo(() => {
    const payload = ayudaPayload ?? {}

    const rawItems =
      payload?.transactions ??
      payload?.data ??
      (Array.isArray(payload) ? payload : [])

    const nextMeta = payload?.meta ?? payload ?? {}
    const nextCurrentPage = Number(nextMeta?.current_page ?? currentPage) || 1
    const nextLastPage = Number(nextMeta?.last_page ?? 1) || 1
    const nextPerPage = Number(nextMeta?.per_page ?? perPage) || perPage
    const nextTotal = Number(nextMeta?.total ?? rawItems?.length ?? 0) || 0

    const items = (Array.isArray(rawItems) ? rawItems : []).map((item) => {
      const program = item?.ayuda_program || item?.program || {}
      const itemStatus = String(item?.status || "")
      const statusLower = itemStatus.toLowerCase()

      // Added
      const amountNumber = parseFloat(
        typeof program.amount === "string" ? program.amount.replace(/,/g, "") : program.amount
      )

      return {
        id: item?.id,
        program_name: program?.title || program?.name || "-",
        applied_date: formatDateOnly(item?.created_at),
        release_date:
          statusLower !== "pending"
            ? formatDateOnly(item?.recieved_at || item?.updated_at)
            : "-",
        // amount:
        //   typeof program?.amount === "number"
        //     ? `₱${program.amount.toLocaleString()}`
        //     : "-",
        amount:
          !isNaN(amountNumber)
            ? `₱${amountNumber.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
            : "-",
        release_status: program?.status || "pending",
        raw: item,
      }
    })

    return {
      records: items,
      meta: {
        current_page: nextCurrentPage,
        last_page: nextLastPage,
        per_page: nextPerPage,
        total: nextTotal,
      },
    }
  }, [ayudaPayload, currentPage, perPage])

  const loading = isLoading || isFetching
  const error =
    ayudaError?.response?.data?.message ||
    ayudaError?.message ||
    (!residentId ? "Resident ID not found" : null)

  const columns = useMemo(
    () => [
      { key: "program_name", label: "Program Name" },
      { key: "applied_date", label: "Applied Date" },
      { key: "release_date", label: "Release Date" },
      { key: "amount", label: "Amount" },
      {
        key: "release_status",
        label: "Release Status",
        render: (value) => {
          const statusValue = String(value || "")
          const v = statusValue.toLowerCase()
          const variant =
            v === "ongoing"
              ? "bg-blue-100 text-blue-500"
              : v === "completed"
                ? "bg-green-100 text-green-500"
                : v === "pending"
                  ? "bg-yellow-100 text-yellow-500"
                  : undefined

          return <StatusBadge status={statusValue} variant={variant} />
        },
      },
      {
        key: "actions",
        label: "Action",
        render: (_, row) => (
          <Button
            variant="ghost"
            size="sm"
            icon={<EyeIcon className="h-4 w-4 text-blue-600" />}
            aria-label="View"
            onClick={() => {
              setSelectedAyuda(row?.raw || null)
              setIsDetailsOpen(true)
            }}
          />
        ),
      },
    ],
    []
  )

  const selectedProgram = selectedAyuda?.ayuda_program || {}
  const selectedStatusLower = String(selectedAyuda?.status || "").toLowerCase()
  const selectedReleaseStatus = (() => {
    if (selectedStatusLower === "pending") return "Pending"
    if (selectedStatusLower === "ongoing") return "Ongoing"
    if (selectedStatusLower === "completed") return "Completed"
    return selectedAyuda?.status ? String(selectedAyuda.status) : "-"
  })()
  const selectedReleaseVariant = (() => {
    const v = selectedReleaseStatus.toLowerCase()
    if (v === "pending") return "bg-yellow-100 text-yellow-800"
    if (v === "ongoing") return "bg-blue-100 text-blue-700"
    if (v === "completed") return "bg-green-100 text-green-800"
    return "bg-gray-100 text-gray-700"
  })()
  const selectedCoverage = selectedProgram?.is_national ? "National" : "Selected Areas"
  const selectedDateApplied = formatDateOnly(selectedAyuda?.created_at)
  const selectedReleaseDate =
    selectedStatusLower === "received"
      ? formatDateTime(selectedAyuda?.released_at || selectedAyuda?.updated_at)
      : "-"

  return (
    <div className="w-auto">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
          <ClipboardDocumentListIcon className="h-5 w-5 text-blue-700" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Ayuda</h2>
          <p className="text-sm text-gray-500">Access your complete history for assistance programs, including past and current ayuda received.</p>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Ayuda Programs</h3>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="w-full sm:w-80">
              <SearchBar
                value={query}
                onChange={(e) => {
                  updateSearchParams({
                    search: e.target.value,
                    page: 1,
                  })
                }}
                placeholder="Search transactions..."
              />
            </div>

            <Button
              variant="outline"
              size="md"
              icon={<FunnelIcon className="h-4 w-4" />}
              onClick={() => setShowFilters((v) => !v)}
            >
              Filters
            </Button>

            <Button
              variant="primary"
              size="md"
              icon={<ArrowDownTrayIcon className="h-4 w-4" />}
              onClick={() => console.log("Export")}
            >
              Export
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
            <div className="md:col-span-1">
              <div className="text-xs font-semibold text-gray-500">Release Date</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) =>
                    updateSearchParams({
                      start_date: e.target.value,
                      page: 1,
                    })
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) =>
                    updateSearchParams({
                      end_date: e.target.value,
                      page: 1,
                    })
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="md:col-span-1">
              <div className="text-xs font-semibold text-gray-500">Release Status</div>
              <select
                value={status}
                onChange={(e) =>
                  updateSearchParams({
                    status: e.target.value,
                    page: 1,
                  })
                }
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">All Activities</option>
                <option value="pending">Pending</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="flex items-end md:col-span-1">
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  updateSearchParams({
                    start_date: "",
                    end_date: "",
                    status: "",
                    page: 1,
                  })
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-4 text-sm text-gray-600">Loading...</div>
        ) : error ? (
          <div className="mt-4 text-sm text-red-600">{error}</div>
        ) : (
          <div className="mt-4">
            <Table
              columns={columns}
              data={records}
              noContainer
              getRowKey={(row) => row.id}
            />

            <div className="mt-4">
              <Pagination
                isLoading={loading}
                currentPage={meta.current_page}
                lastPage={meta.last_page}
                perPage={meta.per_page}
                total={meta.total}
                onPageChange={(page, nextPerPage) => {
                  if (typeof nextPerPage === "number" && nextPerPage !== perPage) {
                    updateSearchParams({
                      per_page: nextPerPage,
                      page: 1,
                    })
                    return
                  }
                  updateSearchParams({ page })
                }}
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false)
          setSelectedAyuda(null)
        }}
        title="Ayuda Details"
        size="4xl"
      >
        <div className="rounded-2xl bg-gray-50 p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <div className="text-sm font-semibold text-gray-500">Program Name</div>
              <div className="mt-2 text-sm text-gray-900">
                {selectedProgram?.title || "-"}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-500">Program Description</div>
              <div className="mt-2 text-sm text-gray-900">
                {selectedProgram?.description || "-"}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-500">Amount</div>
              <div className="mt-2 text-sm text-gray-900">
                {/* {typeof selectedProgram?.amount === "number"
                  ? `₱${selectedProgram.amount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : "-"} */}
                {(() => {
                  const amountNumber = parseFloat(
                    typeof selectedProgram?.amount === "string"
                      ? selectedProgram.amount.replace(/,/g, "")
                      : selectedProgram?.amount
                  )
                  return !isNaN(amountNumber)
                    ? `₱${amountNumber.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                    : "-"
                })()}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-500">Coverage</div>
              <div className="mt-2 text-sm text-gray-900">{selectedCoverage}</div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-500">Date Applied</div>
              <div className="mt-2 text-sm text-gray-900">{selectedDateApplied}</div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-500">Release Date</div>
              <div className="mt-2 text-sm text-gray-900">{selectedReleaseDate}</div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-500">Release Status</div>
              <div className="mt-2">
                <StatusBadge
                  status={selectedReleaseStatus}
                  variant={selectedReleaseVariant}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ResidentAyudaPage